import {
  AGENT_LIST_REQ,
  AGENT_LIST_SUCCESS,
  AGENT_LIST_ERROR,
  AGENT_LIST_RESET_STATUS,
  AGENT_DETAILS_REQ,
  AGENT_DETAILS_SUCCESS,
  AGENT_DETAILS_ERROR,
  DECOMMISSION_AGENTS_REQ,
  DECOMMISSION_AGENTS_SUCCESS,
  DECOMMISSION_AGENTS_ERROR,
  CONTAINER_VIEW_PREFERENCES,
  SAVE_CONTAINER_VIEW_PREFERENCES,
  CONTAINER_IMAGES_VIEW_PREFERENCES,
  SAVE_CONTAINER_IMAGES_VIEW_PREFERENCES,
  UPDATE_AGENT_LOCATION_REQ,
  UPDATE_AGENT_LOCATION_SUCCESS,
  UPDATE_AGENT_LOCATION_ERROR,
  ASSET_SUMMARY_VIEW,
  ASSET_SUMMARY_VIEW_SUCCESS,
  ASSET_SUMMARY_VIEW_ERROR  
} from './agents.type'

import { hostsDetailMetaInfo } from '@/column-header/hosts/hosts-details-meta'
import IQCollection from '@/resources/collections/IQCollection'
import Agent from '@/resources/models/Agent'
import ApiRest from '@/api/api-rest'
import { OrderByEnum } from '@/constants/orderby.constants'
import { AgentStateEnum } from '@/constants/host-status.constants'
import _ from 'lodash'

const state = {
  agentDetailsReqStatus: '',
  agentListReqStatus: '',
  agentList: {}, // all agents
  agentEnvironment: null, // hybrid, windows, linux
  activeAgentList: {}, // all active agents (excludes decommissioned agents)
  decommissionAgentsReqStatus: '',
  updateAgentLocationReqStatus: '',
  activeAgentSummary: {
    assetCount: 0,
    protectedCount: 0,
    unprotectedCount: 0,
    needsAttentionCount: 0,
    progressionCount: 0,
    exposedAgents: []
  },
  containerPreferences: [],
  containerImagesPreferences: [],
  assetSummaryView: {}
}

const getters = {
  agentDetailsReqStatus: state => state.agentDetailsReqStatus,
  agentListReqStatus: state => state.agentListReqStatus,
  agentList: state => state.agentList,
  agentEnvironment: state => state.agentEnvironment,
  activeAgentList: state => state.activeAgentList,
  activeAgentSummary: state => state.activeAgentSummary,
  containerPreferences: state => state.containerPreferences,
  containerImagesPreferences: state => state.containerImagesPreferences,
  assetSummaryView: state => state.assetSummaryView
}

// Best Practice - updates state
const mutations = {
  [AGENT_LIST_REQ]: state => {
    state.agentListReqStatus = 'loading'
  },
  [AGENT_LIST_SUCCESS]: (state, data) => {
    state.agentListReqStatus = 'saved'
    state.agentList = data.allAgents
    state.activeAgentList = data.activeAgents

    state.activeAgentSummary.assetCount = data.activeAgents.models.length
    state.activeAgentSummary.protectedCount = _.filter(data.activeAgents.models, { managed: true }).length
    state.activeAgentSummary.unprotectedCount = _.filter(data.activeAgents.models, { managed: false }).length
    state.activeAgentSummary.needsAttentionCount = _.filter(data.activeAgents.models, { needsAttention: true }).length
    state.activeAgentSummary.atRiskCount = _.filter(data.activeAgents.models, { atRisk: true }).length
    state.activeAgentSummary.progressionCount = _.sum(_.map(data.activeAgents.models, 'numProgressions'))
    state.activeAgentSummary.upgradeableCount = _.filter(data.activeAgents.models, 'upgradeable').length

    // openPortRanges: [{from: 3389, protocol: "tcp", to: 3389}]
    state.activeAgentSummary.exposedAgents = _.filter(data.activeAgents.models, agent => {
      return agent.agentDetails.openToPublicInternet === true
    })

    // detect environment
    let linux = _.find(data.allAgents.models, agent => {
      return agent.system.os === 'linux'
    })
    let windows = _.find(data.allAgents.models, agent => {
      return agent.system.os === 'windows'
    })
    if (linux && windows) {
      state.agentEnvironment = 'hybrid'
    } else if (linux && !windows) {
      state.agentEnvironment = 'linux'
    } else if (!linux && windows) {
      state.agentEnvironment = 'windows'
    }
  },
  [AGENT_LIST_ERROR]: (state, errMsg) => {
    state.agentListReqStatus = 'error'
  },
  [AGENT_DETAILS_REQ]: state => {
    state.agentDetailsReqStatus = 'loading'
  },
  [AGENT_DETAILS_SUCCESS]: state => {
    state.agentDetailsReqStatus = 'saved'
  },
  [AGENT_DETAILS_ERROR]: (state, errMsg) => {
    state.agentDetailsReqStatus = 'error'
  },
  [AGENT_LIST_RESET_STATUS]: state => {
    state.agentListReqStatus = ''
  },
  [DECOMMISSION_AGENTS_REQ]: state => {
    state.decommissionAgentsReqStatus = 'loading'
  },
  [DECOMMISSION_AGENTS_SUCCESS]: (state, data) => {
    state.decommissionAgentsReqStatus = 'saved'
  },
  [DECOMMISSION_AGENTS_ERROR]: (state, errMsg) => {
    state.decommissionAgentsReqStatus = 'error'
  },
  [UPDATE_AGENT_LOCATION_REQ]: state => {
    state.updateAgentLocationReqStatus = 'loading'
  },
  [UPDATE_AGENT_LOCATION_SUCCESS]: (state, data) => {
    state.updateAgentLocationReqStatus = 'saved'
  },
  [UPDATE_AGENT_LOCATION_ERROR]: (state, errMsg) => {
    state.updateAgentLocationReqStatus = 'error'
  },
  [CONTAINER_VIEW_PREFERENCES]: (state, data) => {
    state.containerPreferences = data
  },
  [CONTAINER_IMAGES_VIEW_PREFERENCES]: (state, data) => {
    state.containerImagesPreferences = data
  },
  [ASSET_SUMMARY_VIEW_SUCCESS]: (state, data) => {
    state.assetSummaryView = data
  }
}

// Best Practice - actions call mutations
// make api call and commit states
const actions = {
  [CONTAINER_VIEW_PREFERENCES] (context, uid) {
    return new Promise((resolve, reject) => {
      ApiRest()
        .get('preferences')
        .then(({ data }) => {
          let currUserContainerPrefs = data.find(pref => {
            return pref.tbl_id === 2 && pref.user_id === uid
          })
          context.commit(CONTAINER_VIEW_PREFERENCES, currUserContainerPrefs.prefered_cols)
          resolve(data)
        })
    })
  },
  [CONTAINER_IMAGES_VIEW_PREFERENCES] (context, uid) {
    return new Promise((resolve, reject) => {
      ApiRest()
        .get('preferences')
        .then(({ data }) => {
          let currUserContainerPrefs = data.find(pref => {
            return pref.tbl_id === 19 && pref.user_id === uid
          })
          context.commit(CONTAINER_IMAGES_VIEW_PREFERENCES, currUserContainerPrefs.prefered_cols)
          resolve(data)
        })
    })
  },
  [SAVE_CONTAINER_VIEW_PREFERENCES] (context, payload) {
    return new Promise((resolve, reject) => {
      ApiRest()
        .post('preferences', {
          userId: payload.userId,
          tblId: payload.tblId,
          preferedCols: payload.preferedCols
        })
        .then(resp => {
          resolve(resp)
        })
        .catch(errMsg => {
          reject(errMsg)
        })
    })
  },
  [SAVE_CONTAINER_IMAGES_VIEW_PREFERENCES] (context, payload) {
    return new Promise((resolve, reject) => {
      ApiRest()
        .post('preferences', {
          userId: payload.userId,
          tblId: 19,
          preferedCols: payload.preferedCols
        })
        .then(resp => {
          resolve(resp)
        })
        .catch(errMsg => {
          reject(errMsg)
        })
    })
  },
  [AGENT_LIST_REQ] (context) {
    return new Promise((resolve, reject) => {
      context.commit(AGENT_LIST_REQ)
      ApiRest()
        .get('/assets/vms')
        // .get('static/json/agents.json')
        .then(({ data }) => {
          let iqhubVersion = context.rootState.version.iqhubVersion
          let sortKey = 'numProgressions'
          data = _.chain(data)
            .map(agent => {
              return new Agent('agentId', _.merge(agent, { iqhubVersion: iqhubVersion }))
            })
            .sort((a, b) => {
              return b[sortKey] - a[sortKey]
            })
            .value()

          let allAgents = new IQCollection(
            data,
            { attributes: hostsDetailMetaInfo.meta.columns },
            { defaultSort: sortKey, sortOrder: OrderByEnum.DESC }
          )

          let activeAgents = new IQCollection(
            _.filter(data, agent => {
              // check if devOnly attribute exists (3.1.8+)
              if (_.has(agent, 'devOnly') && agent.devOnly === true) {
                return false
              }
              return agent.state !== AgentStateEnum.DECOMMISSIONED
            }),
            { attributes: hostsDetailMetaInfo.meta.columns },
            { defaultSort: sortKey, sortOrder: OrderByEnum.DESC }
          )

          context.commit(AGENT_LIST_SUCCESS, {
            allAgents: allAgents,
            activeAgents: activeAgents
          })
          resolve(activeAgents)
        })
        .catch(errMsg => {
          context.commit(AGENT_LIST_ERROR, errMsg)
          reject(errMsg)
        })
    })
  },
  [AGENT_DETAILS_REQ] (context, agentId) {
    let agentIdEncoded = encodeURIComponent(agentId)
    return new Promise((resolve, reject) => {
      context.commit(AGENT_DETAILS_REQ)
      ApiRest()
        .get(`/agents/${agentIdEncoded}`)
        .then(({ data }) => {
          let iqhubVersion = context.rootState.version.iqhubVersion
          context.commit(AGENT_DETAILS_SUCCESS)
          resolve(new Agent('agentId', _.merge(data, { iqhubVersion: iqhubVersion })))
        })
        .catch(errMsg => {
          context.commit(AGENT_DETAILS_ERROR, errMsg)
          reject(errMsg)
        })
    })
  },
  [AGENT_LIST_RESET_STATUS] (context) {
    context.commit(AGENT_LIST_RESET_STATUS)
  },
  [DECOMMISSION_AGENTS_REQ] (context, payload) {
    return new Promise((resolve, reject) => {
      context.commit(DECOMMISSION_AGENTS_REQ)
      ApiRest()
        .post('/agents/decommission', {
          agents: payload.agents
        })
        .then(({ data }) => {
          context.commit(DECOMMISSION_AGENTS_SUCCESS, data)
          resolve(data)
        })
        .catch(errMsg => {
          context.commit(DECOMMISSION_AGENTS_ERROR, errMsg)
          reject(errMsg)
        })
    })
  },
  [UPDATE_AGENT_LOCATION_REQ] (context, payload) {
    return new Promise((resolve, reject) => {
      context.commit(UPDATE_AGENT_LOCATION_REQ)
      ApiRest()
        .post('/agents/locations', {
          agents: payload.agents
        })
        .then(({ data }) => {
          context.commit(UPDATE_AGENT_LOCATION_SUCCESS, data)
          resolve(data)
        })
        .catch(errMsg => {
          context.commit(UPDATE_AGENT_LOCATION_ERROR, errMsg)
          reject(errMsg)
        })
    })
  },
  [ASSET_SUMMARY_VIEW] (context) {
    return new Promise((resolve, reject) => {
      ApiRest()
        .get('asset-summary-view')
        .then(({ data }) => {
          context.commit(ASSET_SUMMARY_VIEW_SUCCESS, data)
          resolve(data)
        }).catch(errMsg => {
          context.commit(ASSET_SUMMARY_VIEW_ERROR, errMsg)
          reject(errMsg)
        })
    })
  }
}

export default {
  state,
  getters,
  mutations,
  actions
}



// WEBPACK FOOTER //
// ./src/store/modules/agents/agents.module.js